<template>
  <section
    ref="rootElement"
    class="s-registration"
    data-gtm-container="company-registration-form"
  >
    <form
      v-if="isFormVisible"
      novalidate
      @submit.prevent="handleSubmit(formSubmit)"
    >
      <SButtonGroup
        v-if="['nl-NL', 'be-NL'].includes(locale)"
        v-model="locale"
        class="s-registration__input"
        inline
        :items="localeCodesItems"
        :label="tr('company_registration_form.fields.locale.label')"
        name="locale"
        outlined
        v-on="
          useGtmListeners(
            'locale',
            tr('company_registration_form.fields.locale.label'),
          )
        "
      />

      <SCreditDevice
        v-model:company-name="company"
        v-model:registration-number="registrationNumber"
        class="s-registration__input"
        :locale
        v-on="creditDeviceGtmListeners"
        @update:city="city = $event"
        @update:credit-device-id="creditDeviceId = $event"
        @update:house-number="houseNumber = $event"
        @update:street="street = $event"
        @update:zipcode="zipcode = $event"
      />

      <SProducts
        v-model="products"
        class="s-registration__input"
        :locale="locale"
      />

      <fieldset class="s-registration__input s-registration__input--name">
        <STextInput
          v-model="firstName"
          :label="tr('company_registration_form.fields.firstName.label')"
          :maxlength="255"
          name="firstName"
          required
          v-on="
            useGtmListeners(
              'firstName',
              tr('company_registration_form.fields.firstName.label'),
            )
          "
        />
        <STextInput
          v-model="lastName"
          :label="tr('company_registration_form.fields.lastName.label')"
          :maxlength="255"
          name="lastName"
          required
          v-on="
            useGtmListeners(
              'lastName',
              tr('company_registration_form.fields.lastName.label'),
            )
          "
        />
      </fieldset>
      <SEmail
        v-model="email"
        class="s-registration__input--email"
        :error-label="tr('company_registration_form.fields.email.errorLabel')"
        :label="tr('company_registration_form.fields.email.label')"
        name="email"
        required
        :typo-correction="locale"
        v-on="
          useGtmListeners(
            'email',
            tr('company_registration_form.fields.email.label'),
          )
        "
      />

      <STextInput
        v-model="phone"
        class="s-registration__input"
        :error-label="tr('company_registration_form.fields.phone.errorLabel')"
        :label="tr('company_registration_form.fields.phone.label')"
        name="phone"
        required
        type="tel"
        v-on="
          useGtmListeners(
            'phone',
            tr('company_registration_form.fields.phone.label'),
          )
        "
      />

      <SCheckbox
        v-model="termsAndConditions"
        class="s-registration__input"
        :label="tr('company_registration_form.fields.termsAndConditions.label')"
        name="termsAndConditions"
        required
        v-on="
          useGtmListeners(
            'termsAndConditions',
            tr('company_registration_form.fields.termsAndConditions.label'),
          )
        "
      />

      <SHoneypot v-model="honeypot" />

      <RegistrationFailed v-if="failedType" :failed-type="failedType" />

      <SButton
        class="s-registration__submit"
        color="accent"
        :loading="isPending"
        suffix-icon="fa-regular fa-arrow-right"
        type="submit"
      >
        {{ tr("company_registration_form.submit") }}
      </SButton>
    </form>
    <RegistrationSuccess v-if="isSuccessful" />
  </section>
</template>

<script setup lang="ts">
import type { PropType } from "vue";

import { useMutation } from "@pinia/colada";
import { computed, reactive, ref, useTemplateRef, watchEffect } from "vue";

import { getMetadataFromArgus } from "@solvari/argus";
import { SButton, SButtonGroup, SCheckbox } from "@solvari/common-fe";
import { emitWindowRedirect } from "@solvari/common-fe/helpers";
import { useValidationObserver } from "@solvari/common-fe/validation";
import { SCreditDevice, SEmail, STextInput } from "@solvari/components";
import {
  provideI18nLocale,
  useI18nCommonFe,
  useI18nCompanyRegistrationForm,
} from "@solvari/translations";
import { getEnv, isError } from "@solvari/utils";

import {
  submit,
  UserAlreadyAssignedToCustomerException,
} from "@/api/registration";
import RegistrationFailed from "@/components/RegistrationFailed.vue";
import RegistrationSuccess from "@/components/RegistrationSuccess.vue";
import SHoneypot from "@/components/SHoneypot.vue";
import SProducts from "@/components/SProduct.vue";
import { useGtmListeners } from "@/composables/useGtmListeners";
import { useGtmStore } from "@/plugins/pinia/gtm";

const props = defineProps({
  campaignId: { type: Number as PropType<number | null>, default: null },
  affiliateId: { type: Number as PropType<number | null>, default: null },
  iframeId: { type: Number as PropType<number | null>, default: null },
});

const locale = ref(getEnv().config.localeCode);
provideI18nLocale(locale);
const { tr } = useI18nCompanyRegistrationForm();
const { tr: trCommonFe } = useI18nCommonFe();

const rootElement = useTemplateRef("rootElement");
const gtmStore = useGtmStore();

const {
  status,
  state,
  isLoading,
  mutateAsync: submitForm,
} = useMutation(submit);

watchEffect(() => (gtmStore.rootElement = rootElement.value));
const companyNameGtmListeners = useGtmListeners(
  "companyName",
  trCommonFe("common_fe.fields.company_name.label"),
);
const registrationNumberGtmListeners = useGtmListeners(
  "registrationNumber",
  trCommonFe("common_fe.fields.registration_number.label"),
);

const creditDeviceGtmListeners = {
  focus: (name: string) =>
    name === "companyName"
      ? companyNameGtmListeners.focus()
      : registrationNumberGtmListeners.focus(),
  validationError: ({ name, error }: { error: string | null; name: string }) =>
    name === "companyName"
      ? companyNameGtmListeners.validationError({ error })
      : registrationNumberGtmListeners.validationError({ error }),
};

const { handleSubmit } = useValidationObserver();

const urlSearchParams = new URLSearchParams(window.location.search);

const getProductsParams = computed<number[]>(() => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const productsParam = urlSearchParams.get("products") || "[]";
  try {
    return JSON.parse(productsParam) as number[];
  } catch (_error) {
    return [];
  }
});

const firstName = ref(urlSearchParams.get("firstName") || "");
const lastName = ref(urlSearchParams.get("lastName") || "");
const email = ref(urlSearchParams.get("email") || "");
const phone = ref(urlSearchParams.get("phone") || "");
const termsAndConditions = ref(false);
const products = ref<number[]>(getProductsParams.value);

const honeypot = ref(false);

const company = ref(urlSearchParams.get("company") || "");
const registrationNumber = ref("");

const creditDeviceId = ref<number | null>(null);
const street = ref<string | null>(null);
const houseNumber = ref<string | null>(null);
const zipcode = ref<string | null>(null);
const city = ref<string | null>(null);

const isPending = computed(() => isLoading.value);
const isSuccessful = computed(
  () =>
    status.value === "success" &&
    state.value.data &&
    !isError(state.value.data),
);
const failedType = ref<"emailExists" | "serverError" | null>(null);
const isFormVisible = computed(() => !isSuccessful.value);

const localeCodesItems = computed(() => [
  { label: tr("company_registration_form.locale.nl-NL"), value: "nl-NL" },
  { label: tr("company_registration_form.locale.nl-BE"), value: "be-NL" },
]);

async function formSubmit() {
  const metadata = await getMetadataFromArgus();

  const application = {
    campaignId: props.campaignId,
    affiliateId: props.affiliateId,
    iframeId: props.iframeId,
  };

  const lead = reactive({
    products,
    firstName,
    lastName,
    email,
    phone,

    company,
    registrationNumber,

    creditDeviceId,
    street,
    houseNumber,
    zipcode,
    city,

    localeCode: locale.value,
    termsAndConditions,
  });

  const result = await submitForm({ ...lead, ...application, ...metadata });

  if (isError(result)) {
    void gtmStore.registrationErrorEvent({
      name: "submitButton",
      label: tr("company_registration_form.submit"),
      error: result.message,
    });

    if (result instanceof UserAlreadyAssignedToCustomerException) {
      failedType.value = "emailExists";
      return;
    }

    failedType.value = "serverError";
    return;
  }

  await gtmStore.generateCompanyEvent({
    ...lead,
    customerId: result.customerId,
  });

  if (result.redirectUrl) {
    if (result.shouldRedirectWindow) {
      emitWindowRedirect(result.redirectUrl);
    } else {
      window.location.href = result.redirectUrl;
    }
  }
}
</script>

<style lang="postcss">
.s-registration {
  @apply font-primary;

  &__input {
    @apply mb-6;

    &--name {
      @apply grid gap-6 grid-cols-auto-64 grid-cols-auto-fit grid-cols-auto-grow;
    }

    &--email {
      @apply mb-1.5;
    }
  }

  &__submit {
    @apply ml-auto;
  }
}
</style>
